import { useCallback, type MutableRefObject } from 'react';

import { VALID_DATE_FORMATS } from '@adsk/alloy-react-datetime';
import { noop } from '@adsk/alloy-react-helpers';

import useDateRangeSelection from './useDateRangeSelection';
import useKeyDownHandler from './useKeyDownHandler';
import useSharedDateLogic from './useSharedDateLogic';
import { DATE_PICKER_VARIANTS } from '../consts';
import { getFieldValue } from '../helpers';
import type { UseDatePickerCommonArgs, DateRangePickerArgs } from '../types';

export type UseDateRangePickerProps = UseDatePickerCommonArgs &
  DateRangePickerArgs & {
    target?: MutableRefObject<HTMLDivElement>;
    setShouldShowSelection: React.Dispatch<React.SetStateAction<boolean>>;
  };

function useDateRangePicker({
  /** core props */
  startDate: propsStartDate,
  endDate: propsEndDate,
  onChange: propsOnChange = noop,
  initialStartDate = null,
  initialEndDate = null,
  allowSameStartAndEndDate = false,
  /** picker props */
  variant = DATE_PICKER_VARIANTS.FIELD,
  /** trigger props */
  error,
  format = VALID_DATE_FORMATS.NUMERIC,
  placeholder,
  startDatePlaceholder,
  endDatePlaceholder,
  triggerProps,
  setShouldShowSelection,
  ...props
}: UseDateRangePickerProps) {
  const {
    T,
    locale,
    minDate,
    maxDate,
    open,
    toggleOverlay,
    placement,
    arrowProps,
    targetProps,
    overlayProps,
    onFinalSelection,
    context,
  } = useSharedDateLogic(props);

  const { startDate, endDate, onDateSelected, resetSelection } =
    useDateRangeSelection({
      initialStartDate,
      initialEndDate,
      propsStartDate,
      propsEndDate,
      propsOnChange,
      allowSameStartAndEndDate,
      onFinalSelection,
      setShouldShowSelection,
    });

  useKeyDownHandler({ open, resetSelection });

  const getTriggerProps = useCallback(() => {
    return variant === DATE_PICKER_VARIANTS.FIELD
      ? {
          disabled: props.disabled,
          error,
          range: true,
          open,
          variant,
          placeholder:
            placeholder?.toString() ?? T.translate('SELECT_DATE')?.toString(),
          startDatePlaceholder:
            startDatePlaceholder ??
            placeholder ??
            T.translate('START_DATE')?.toString(),
          endDatePlaceholder:
            endDatePlaceholder ??
            placeholder ??
            T.translate('END_DATE')?.toString(),
          value: getFieldValue({
            startDate,
            endDate,
            range: true,
            format,
            locale,
          }),
          ...triggerProps,
          'data-testid': 'DateRangePickerTarget',
        }
      : {
          variant,
          disabled: props.disabled,
          ...triggerProps,
          'data-testid': 'DateRangePickerTarget',
        };
  }, [
    variant,
    props.disabled,
    error,
    open,
    placeholder,
    T,
    startDatePlaceholder,
    endDatePlaceholder,
    startDate,
    endDate,
    format,
    locale,
    triggerProps,
  ]);

  return {
    context,
    minDate,
    maxDate,
    open,
    variant,
    endDate,
    startDate,
    toggleOverlay,
    getTriggerProps,
    onDateSelected,
    resetSelection,
    // useOverlay
    placement,
    arrowProps,
    targetProps,
    overlayContainerProps: overlayProps,
  };
}

export default useDateRangePicker;
